import { OBJECT_TAGS } from '@constants/objectTag';
import { type Inspection } from '@lib/spproject/types';
import { useAppSelector } from '@store/hooks';
import React, { useEffect, useState } from 'react';
import { Color, Group, Vector2, Vector3 } from 'three';
import { Line2, LineGeometry, LineMaterial } from 'three-stdlib';

const DrawLines: React.FC = () => {
  const [drawLinesGroup, setDrawLinesGroup] = useState<Group | null>(null);
  const viewerState = useAppSelector((state) => state.viewer);
  const { selectedPOI, stepIndex } = viewerState;

  useEffect(() => {
    drawLinesGroup?.clear();

    if (selectedPOI && drawLinesGroup) {
      drawLines(selectedPOI?.steps[stepIndex].drawings ?? [], drawLinesGroup);
    }
  }, [selectedPOI, drawLinesGroup, stepIndex]);

  return <group ref={(group) => setDrawLinesGroup(group)} />;
};

export default DrawLines;

const drawLines = (drawings: Inspection.Drawing[], group: Group | null) => {
  for (const drawing of drawings ?? []) {
    const centerPosition = new Vector3(drawing.position.x, drawing.position.y, drawing.position.z).transformPosition();

    const resolution = new Vector2(window.innerWidth, window.innerHeight);

    const pts: number[] = [];

    for (const point of drawing.points) {
      const pt = new Vector3(point.x, point.y, point.z).transformPosition();
      pts.push(pt.x, pt.y, pt.z);
    }

    const hexColor = drawing.color.getHexColor();
    const alpha = drawing.color.getAlpha();

    const material = new LineMaterial({
      color: new Color(hexColor).getHex(),
      opacity: alpha,
      transparent: true,  // alpha < 1.0,
      resolution,
      linewidth: drawing.width,
      depthWrite: false,
      depthTest: true,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -1,
    });

    const geometry = new LineGeometry();
    geometry.setPositions(pts);

    const line = new Line2(geometry, material);
    line.computeLineDistances();

    const lineGroup = new Group();
    lineGroup.position.set(centerPosition.x, centerPosition.y, centerPosition.z);
    lineGroup.add(line);
    lineGroup.userData = { tag: OBJECT_TAGS.DRAWING_LINES, id: drawing.id };

    group?.add(lineGroup);
  }
};
